import React, { useEffect, useState, useMemo, useCallback, memo } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import {
  Users,
  Mic2,
  ArrowRight,
  Clock,
  ListChecks,
  Play,
  X,
} from "lucide-react";
import { toast } from "sonner";
import BrandLogo from "@/components/BrandLogo/BrandLogo";
import NetworkingDetailsModal from "@/components/NetworkingDetailsModal";
import { joinSessionTable } from "@/Services/EventMeetingServices";

// ---------------------------------------------------------------------------
// PURE UTILITIES — module-scope
// ---------------------------------------------------------------------------

function seatPositions(count, radius = 92) {
  const items = [];
  for (let i = 0; i < count; i++) {
    const angle = (i / count) * Math.PI * 2 - Math.PI / 2;
    items.push({
      x: Math.cos(angle) * radius,
      y: Math.sin(angle) * radius,
      angle,
    });
  }
  return items;
}

function stripHtml(html) {
  if (!html) return "";
  return String(html)
    .replace(/<[^>]*>/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const ALLOWED_TAGS_RE =
  /<\/?(b|i|em|strong|u|s|br|p|ul|ol|li|h[1-6]|span|div|a|blockquote)\b[^>]*>/gi;

function sanitizeHtml(html) {
  if (!html) return "";

  const decoded = String(html)
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, "\u00A0");

  const withOnlyAllowed = decoded.replace(/<[^>]*>/g, (tag) =>
    ALLOWED_TAGS_RE.test(tag) ? tag : "",
  );

  ALLOWED_TAGS_RE.lastIndex = 0;

  return withOnlyAllowed
    .replace(/\s+on\w+="[^"]*"/gi, "")
    .replace(/\s+on\w+='[^']*'/gi, "")
    .replace(/href="javascript:[^"]*"/gi, 'href="#"')
    .replace(/href='javascript:[^']*'/gi, "href='#'");
}

function hasHtml(str) {
  return /<[a-z][\s\S]*>/i.test(String(str || ""));
}

function htmlOrText(content) {
  if (!content) return { children: "" };
  if (hasHtml(content)) {
    return { dangerouslySetInnerHTML: { __html: sanitizeHtml(content) } };
  }
  return { children: content };
}

// ---------------------------------------------------------------------------
// HTML DISPLAY COMPONENT
// ---------------------------------------------------------------------------

const HtmlContent = memo(function HtmlContent({
  content,
  className = "",
  as: Tag = "div",
  style,
  "data-testid": testId,
}) {
  if (!content) return null;

  const props = {
    className,
    style,
    "data-testid": testId,
  };

  if (hasHtml(content)) {
    return (
      <Tag
        {...props}
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(content) }}
      />
    );
  }

  return <Tag {...props}>{content}</Tag>;
});

// ---------------------------------------------------------------------------
// SPONSOR MOMENT MODAL
// ---------------------------------------------------------------------------

const SponsorMomentModal = memo(function SponsorMomentModal({
  tableId,
  sponsor,
  onClose,
}) {
  const url = sponsor.welcome_video_url || "";

  const embedSrc = useMemo(() => {
    try {
      const ym = url.match(
        /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/,
      );
      if (ym) return `https://www.youtube.com/embed/${ym[1]}?autoplay=1`;
      const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      if (vm) return `https://player.vimeo.com/video/${vm[1]}?autoplay=1`;
    } catch {
      /* noop */
    }
    return null;
  }, [url]);

  return (
    <div
      data-testid={`sponsor-moment-modal-${tableId}`}
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/75 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-[min(96vw,720px)] rounded-3xl border border-[#7BC8A6]/40 bg-[#0A1D1A] shadow-2xl overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-3 min-w-0">
            <BrandLogo
              company={sponsor.company}
              logo={sponsor.logo}
              size={32}
            />
            <div className="min-w-0">
              <p className="overline text-[10px] text-[#7BC8A6]">
                Sponsor moment · 15s
              </p>
              <p className="font-display text-lg tracking-tight truncate">
                {stripHtml(sponsor.company)}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid={`sponsor-moment-close-${tableId}`}
            className="text-white/55 hover:text-white p-1.5 rounded-full hover:bg-white/[0.06]"
            aria-label="Close sponsor moment"
          >
            <X size={18} />
          </button>
        </div>

        <div className="aspect-video bg-black">
          {embedSrc ? (
            <iframe
              src={embedSrc}
              title={`${stripHtml(sponsor.company)} sponsor moment`}
              data-testid={`sponsor-moment-iframe-${tableId}`}
              className="w-full h-full"
              frameBorder="0"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <video
              src={url}
              autoPlay
              controls
              data-testid={`sponsor-moment-video-${tableId}`}
              className="w-full h-full"
            />
          )}
        </div>

        <div className="px-6 py-4 flex items-center justify-between gap-3 border-t border-white/[0.06]">
          <Link
            to={`/brands/${encodeURIComponent(stripHtml(sponsor.company))}`}
            state={{ sponsorData: sponsor.sponsorData }}
            data-testid={`sponsor-moment-profile-${tableId}`}
            className="text-xs text-[#C6A87C] hover:text-[#D9BC93] inline-flex items-center gap-1"
          >
            See {stripHtml(sponsor.company)}'s full profile{" "}
            <ArrowRight size={11} />
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-white/55 hover:text-white"
          >
            Back to the table
          </button>
        </div>
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
// TABLE CARD
// ---------------------------------------------------------------------------

export default function TableCard({
  table,
  joined,
  onJoin,
  onSwitch,
  onAttendeeClick,
  onViewAgenda,
  sponsors = [],
  event = null,
  pulse = false,
}) {
  const navigate = useNavigate();
  const seats = Number(table?.seats || 0) || 8;
  const taken = Number(table?.taken || 0);
  const seatsLeft = seats - taken;
  const waitlistCount = (table?.waitlist || []).length;

  const positions = useMemo(() => seatPositions(seats), [seats]);

  const contextLabel = useMemo(() => {
    const eventName = stripHtml(event?.title || event?.eventName || "");
    const sessionName = stripHtml(table?.session_name || "");
    const parts = [eventName, sessionName].filter(Boolean);
    if (parts.length > 0) return parts.join(" · ");
    return table?.circle && table.circle !== "Tables"
      ? stripHtml(table.circle)
      : "Parallel Conversation";
  }, [event, table]);

  const sponsorMoment = useMemo(
    () => sponsors.find((s) => (s.welcome_video_url || "").trim()),
    [sponsors],
  );

  const { displayImage, displayText, tableSponsor } = useMemo(() => {
    // Priority: sponsorDetails.image > table.tableIcon > sponsor logo
    const sponsorImage = table?.sponsorDetails?.image;
    const tableIcon = table?.tableIcon || table?.table_icon || table?.icon;
    const sp = sponsors.find((s) => s.logo || s.image || s.company);

    return {
      displayImage: sponsorImage || tableIcon || sp?.image || sp?.logo,
      displayText: stripHtml(
        table?.sponsorDetails?.title || table?.circle || sp?.company || "",
      ),
      tableSponsor: table?.sponsorDetails || sp,
    };
  }, [table, sponsors]);

  // Meeting URL — used for navigation after successful join
  const meetingUrl = useMemo(() => {
    const eventId = table.event_id;
    const sessionId = table.session_id;
    const tableId = table.id;
    if (!eventId || !sessionId || !tableId) return null;
    return `/events/${eventId}/sessions/${sessionId}/tables/${tableId}`;
  }, [table.event_id, table.session_id, table.id]);

  // ---------------------------------------------------------------------------
  // Local UI state
  // ---------------------------------------------------------------------------
  const [waitlistEmail, setWaitlistEmail] = useState("");
  const [waitlistName, setWaitlistName] = useState("");
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [waitlistDone, setWaitlistDone] = useState(false);
  const [momentOpen, setMomentOpen] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);

  // Networking modal state
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [joinLoading, setJoinLoading] = useState(false);
  const [lastNetworkingDetails, setLastNetworkingDetails] = useState({
    networkingRole: "",
    networkingIntent: "",
  });

  // ---------------------------------------------------------------------------
  // Stable handlers
  // ---------------------------------------------------------------------------

  const handleAgendaClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      onViewAgenda?.();
    },
    [onViewAgenda],
  );

  const handleMomentOpen = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setMomentOpen(true);
  }, []);

  const handleMomentClose = useCallback(() => setMomentOpen(false), []);

  const handleWaitlistToggle = useCallback(
    () => setWaitlistOpen((v) => !v),
    [],
  );

  // Step 1 — open networking modal instead of joining directly
  const handleJoinClick = useCallback(() => {
    setDetailsModalOpen(true);
  }, []);

  // Step 2 — modal cancelled
  const handleNetworkingCancel = useCallback(
    () => setDetailsModalOpen(false),
    [],
  );

  // Step 3 — modal submitted → call API → navigate
  const handleNetworkingSubmit = useCallback(
    async (details) => {
      // Cache so modal pre-fills next time
      setLastNetworkingDetails(details);
      setDetailsModalOpen(false);

      if (!table.session_id || !table.id) {
        toast.error("Unable to join table — missing session information.");
        return;
      }

      try {
        setJoinLoading(true);

        // ── API call ──────────────────────────────────────────────────────
        // POST /sessions/{sessionId}/tables/{tableId}/join
        // URL  → table.session_id, table.id
        // Body → { networkingRole, networkingIntent }
        // ─────────────────────────────────────────────────────────────────
        const response = await joinSessionTable(
          table.session_id, // → URL param :sessionId
          table.id, // → URL param :tableId
          {
            networkingRole: details.networkingRole, // → request body
            networkingIntent: details.networkingIntent, // → request body
          },
        );

        // Notify parent (refresh table list, update Redux, etc.)
        onJoin?.({
          sessionId: table.session_id,
          tableId: table.id,
          networkingRole: details.networkingRole,
          networkingIntent: details.networkingIntent,
          response, // full API response available to parent
        });

        toast.success("Joined table successfully!");

        // Navigate to the meeting room, forwarding the full API response
        // so the room page has tokens + permissions immediately without
        // making another API call.
        //
        // response.data contains:
        //   tableToken, broadcastToken, tableRoomName, broadcastRoomName,
        //   livekitServerUrl, permissions, participants, sponsorDetails …
        navigate(meetingUrl, {
          state: {
            joinResponse: response?.data ?? response,
            networkingRole: details.networkingRole,
            networkingIntent: details.networkingIntent,
          },
        });
      } catch (err) {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          "Failed to join table. Please try again.";
        toast.error(message);
      } finally {
        setJoinLoading(false);
      }
    },
    [onJoin, meetingUrl, navigate, table.session_id, table.id],
  );

  const submitWaitlist = useCallback(
    async (e) => {
      e.preventDefault();
      setWaitlistDone(true);
      toast.success(`You're on the waitlist for "${stripHtml(table.topic)}".`);
    },
    [table.topic],
  );

  const namedSponsor = useMemo(
    () => sponsors.find((s) => (s.sponsor_table_name || "").trim()),
    [sponsors],
  );

  const modSponsor = useMemo(
    () => sponsors.find((s) => (s.sponsor_moderator_name || "").trim()),
    [sponsors],
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div
      data-testid={`table-card-${table.id}`}
      data-pulse={pulse ? "1" : "0"}
      className={`glass rounded-2xl p-7 md:p-8 relative overflow-hidden flex flex-col h-full transition-all duration-300 ease-out hover:-translate-y-1 hover:border-white/20 group ${
        pulse ? "table-card-pulse" : ""
      }`}
    >
      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-5">
        <div className="min-w-0 flex-1 pr-3">
          <p
            className="overline mb-2 text-[#C6A87C] truncate"
            title={contextLabel}
          >
            {contextLabel}
          </p>

          <HtmlContent
            as="h3"
            content={table.topic}
            className="font-display text-2xl tracking-tight leading-tight max-w-[18ch]"
            data-testid={`table-topic-${table.id}`}
          />

          {namedSponsor && (
            <p
              className="mt-1.5 text-sm text-[#C6A87C] italic max-w-[22ch] leading-snug"
              data-testid={`table-sponsor-name-${table.id}`}
              title={`Sponsored as · ${stripHtml(namedSponsor.sponsor_table_name)} · by ${stripHtml(namedSponsor.company)}`}
            >
              Sponsored as · &ldquo;
              {stripHtml(namedSponsor.sponsor_table_name)}&rdquo;
            </p>
          )}

          {modSponsor && (
            <p
              className="mt-1 text-[11px] text-white/55 flex items-center gap-1.5"
              data-testid={`table-sponsor-moderator-${table.id}`}
            >
              <Mic2 size={10} className="text-[#C6A87C]" aria-hidden="true" />
              Moderated by {stripHtml(modSponsor.sponsor_moderator_name)}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {onViewAgenda && (
            <button
              onClick={handleAgendaClick}
              data-testid={`table-agenda-${table.id}`}
              title="View table agenda"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] border border-white/15 text-xs text-white/85 hover:border-[#C6A87C]/45 hover:text-[#C6A87C] transition-colors"
            >
              <ListChecks size={13} aria-hidden="true" /> Agenda
            </button>
          )}
          {table.is_live && (
            <span className="overline flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#2A7E5D]/15 border border-[#2A7E5D]/40 text-[#7BC8A6]">
              <span
                className="w-1.5 h-1.5 rounded-full bg-[#7BC8A6] ring-live"
                aria-hidden="true"
              />
              Live
            </span>
          )}
        </div>
      </div>

      {/* ── Description ── */}
      <div className="mb-5">
        <HtmlContent
          content={table.description}
          className={`text-white/60 text-sm leading-relaxed prose-sm prose-invert max-w-none
            [&_b]:text-white/80 [&_strong]:text-white/80
            [&_i]:text-white/65 [&_em]:text-white/65
            [&_a]:text-[#C6A87C] [&_a]:underline
            ${!showFullDescription ? "line-clamp-3 overflow-hidden" : ""}`}
          data-testid={`table-description-${table.id}`}
        />

        {stripHtml(table.description).length > 120 && (
          <button
            type="button"
            onClick={() => setShowFullDescription((v) => !v)}
            className="mt-2 text-xs text-[#C6A87C] hover:text-[#D9BC93]"
          >
            {showFullDescription ? "Read Less" : "Read More"}
          </button>
        )}
      </div>

      {/* ── Event metadata ── */}
      {event && typeof event.registered === "number" && (
        <div
          className="flex items-center gap-3 text-xs text-white/75 mb-5 flex-wrap"
          data-testid={`table-event-meta-${table.id}`}
        >
          <span
            className="flex items-center gap-1.5 text-white/60"
            data-testid={`table-event-registered-${table.id}`}
          >
            <Users size={11} aria-hidden="true" /> {event.registered} registered
            for event
          </span>
        </div>
      )}

      {/* ── Sponsors strip ── */}
      {sponsors.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-3 mb-6"
          data-testid={`table-sponsors-${table.id}`}
        >
          <span className="overline">Sponsored by</span>
          {sponsors.map((s) => {
            const companyName = stripHtml(s.company);
            return (
              <Link
                key={s.id}
                to={`/brands/${encodeURIComponent(companyName)}`}
                state={{ sponsorData: s.sponsorData }}
                data-testid={`table-sponsor-link-${table.id}-${companyName}`}
                title={`See ${companyName}'s profile`}
                className="flex items-center gap-2 text-xs px-2.5 py-1 rounded-full bg-[#C6A87C]/10 border border-[#C6A87C]/30 text-[#C6A87C] hover:bg-[#C6A87C]/20 hover:border-[#C6A87C]/55 transition-colors"
              >
                {s.image ? (
                  <img
                    src={s.image}
                    alt={companyName}
                    className="w-[18px] h-[18px] object-contain rounded"
                  />
                ) : (
                  <BrandLogo company={companyName} size={18} />
                )}
                {companyName}
              </Link>
            );
          })}

          {sponsorMoment && (
            <button
              type="button"
              onClick={handleMomentOpen}
              data-testid={`table-sponsor-moment-${table.id}`}
              title={`Play ${stripHtml(sponsorMoment.company)}'s sponsor moment`}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#7BC8A6]/10 border border-[#7BC8A6]/45 text-[#7BC8A6] hover:bg-[#7BC8A6]/20 hover:border-[#7BC8A6]/70 transition-colors"
            >
              <Play size={11} className="fill-current" aria-hidden="true" />
              Sponsor moment
            </button>
          )}
        </div>
      )}

      {/* ── 3-D Round Table ── */}
      <div
        className="relative mx-auto w-[260px] h-[230px] mb-6"
        style={{ perspective: "900px" }}
        aria-hidden="true"
      >
        <div
          className="absolute inset-0"
          style={{
            transformStyle: "preserve-3d",
            transform: "rotateX(45deg)",
          }}
        >
          {/* Shadow disc */}
          <div
            className="absolute"
            style={{
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%) translateZ(-12px)",
              width: 200,
              height: 200,
              borderRadius: "50%",
              background: "rgba(0,0,0,0.35)",
              filter: "blur(18px)",
            }}
          />

          {/* Table surface */}
          <div
            className="absolute"
            style={{
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: 200,
              height: 200,
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 35% 30%, #E7D9C0 0%, #C6A87C 35%, #8A6F4A 75%, #5A4530 100%)",
              boxShadow:
                "inset 0 0 0 2px rgba(60,40,20,0.45), inset 0 -6px 18px rgba(0,0,0,0.35), 0 6px 14px rgba(0,0,0,0.5)",
              border: "1px solid rgba(0,0,0,0.4)",
            }}
          />

          {/* Inner ring */}
          <div
            className="absolute"
            style={{
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%) translateZ(0.5px)",
              width: 140,
              height: 140,
              borderRadius: "50%",
              border: "1px solid rgba(60,40,20,0.35)",
              opacity: 0.7,
            }}
          />

          {/* Centre plate */}
          {(() => {
            const plate = (
              <div
                data-testid={`table-center-${table.id}`}
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  transform: "translate(-50%, -50%) translateZ(1px)",
                  width: 84,
                  height: 84,
                  borderRadius: "50%",
                  background: displayImage
                    ? "radial-gradient(circle at 35% 30%, #fdf6e8 0%, #f3e6cd 50%, #d9c599 100%)"
                    : "rgba(20,12,4,0.35)",
                  border: displayImage
                    ? "1px solid rgba(60,40,20,0.45)"
                    : "1.5px dashed rgba(255,255,255,0.18)",
                  boxShadow: displayImage
                    ? "inset 0 -2px 6px rgba(0,0,0,0.25)"
                    : "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                {displayImage ? (
                  <img
                    src={displayImage}
                    alt={displayText}
                    style={{
                      maxWidth: "70%",
                      maxHeight: "70%",
                      objectFit: "contain",
                      filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.25))",
                    }}
                  />
                ) : displayText ? (
                  <span
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontSize: 14,
                      color: "rgba(60,40,20,0.85)",
                      padding: "0 8px",
                      lineHeight: 1.05,
                      textAlign: "center",
                    }}
                  >
                    {displayText}
                  </span>
                ) : null}
              </div>
            );

            if (displayText && tableSponsor) {
              return (
                <Link
                  to={`/brands/${encodeURIComponent(displayText)}`}
                  state={{ sponsorData: tableSponsor.sponsorData }}
                  title={`See ${displayText}'s profile`}
                  data-testid={`table-center-link-${table.id}`}
                  style={{ position: "absolute", inset: 0, display: "block" }}
                >
                  {plate}
                </Link>
              );
            }
            return plate;
          })()}

          {/* Chairs */}
          {positions.map((p, i) => (
            <div
              key={i}
              className="absolute"
              style={{
                left: "50%",
                top: "50%",
                transform: `translate(-50%, -50%) translate(${p.x}px, ${p.y}px) translateZ(6px) rotate(${(p.angle * 180) / Math.PI + 90}deg)`,
              }}
            >
              <div
                style={{
                  width: 24,
                  height: 18,
                  borderRadius: "6px 6px 4px 4px",
                  background:
                    "linear-gradient(180deg, #2a3a36 0%, #1a2522 100%)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  boxShadow:
                    "0 2px 4px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  left: "50%",
                  top: -2,
                  transform: "translateX(-50%) rotateX(-50deg)",
                  transformOrigin: "bottom center",
                  width: 20,
                  height: 12,
                  borderRadius: "4px 4px 2px 2px",
                  background:
                    "linear-gradient(180deg, #3a4a45 0%, #1f2c28 100%)",
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              />
            </div>
          ))}
        </div>

        {/* ── Avatars layer ── */}
        <div className="absolute inset-0">
          {table.moderator && table.moderator_avatar && (
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
              <button
                type="button"
                onClick={() => onAttendeeClick?.(stripHtml(table.moderator))}
                className="flex flex-col items-center cursor-pointer"
                data-testid={`table-${table.id}-moderator`}
                title={`Moderator: ${stripHtml(table.moderator)}`}
              >
                <img
                  src={table.moderator_avatar}
                  alt={stripHtml(table.moderator)}
                  className="w-12 h-12 rounded-full object-cover border-2 border-[#C6A87C] float-y shadow-lg"
                  style={{ boxShadow: "0 6px 14px -4px rgba(0,0,0,0.5)" }}
                />
                <span className="overline mt-1.5 flex items-center gap-1 text-[10px]">
                  <Mic2 size={9} aria-hidden="true" /> Mod
                </span>
              </button>
            </div>
          )}

          {positions.map((p, i) => {
            const attendee = (table.attendees || [])[i];
            const isOccupied = i < taken; // Check if this seat index is within occupied count
            const name = stripHtml(attendee?.name || "");
            const initials =
              attendee?.initials ||
              (name ? name.slice(0, 2).toUpperCase() : "?");

            return (
              <div
                key={i}
                className="absolute top-1/2 left-1/2"
                style={{
                  transform: `translate(${p.x - 14}px, ${p.y * 0.72 - 38}px)`,
                }}
              >
                {attendee ? (
                  // ✅ Participant details available - show avatar/initials
                  <button
                    type="button"
                    onClick={() => onAttendeeClick?.(name)}
                    className="w-7 h-7 rounded-full overflow-hidden border border-white/30 float-y hover:border-[#C6A87C] hover:scale-110 transition-all shadow"
                    style={{
                      animationDelay: `${i * 0.3}s`,
                      boxShadow: "0 3px 8px -2px rgba(0,0,0,0.5)",
                    }}
                    title={name}
                    data-testid={`table-${table.id}-seat-${i}`}
                  >
                    {attendee.avatar ? (
                      <img
                        src={attendee.avatar}
                        alt={name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="w-full h-full flex items-center justify-center text-[10px] font-semibold bg-[#0A1D1A] text-[#C6A87C]">
                        {initials}
                      </span>
                    )}
                  </button>
                ) : isOccupied ? (
                  // ✅ Seat is occupied but no participant details - show golden circle
                  <div
                    className="w-7 h-7 rounded-full border-2 border-[#C6A87C] bg-gradient-to-br from-[#C6A87C] to-[#8A6F4A] float-y shadow-lg"
                    style={{
                      animationDelay: `${i * 0.3}s`,
                      boxShadow:
                        "0 3px 8px -2px rgba(198, 168, 124, 0.6), inset 0 1px 2px rgba(255,255,255,0.3)",
                    }}
                    title="Occupied seat"
                    aria-label="Occupied seat"
                    data-testid={`table-${table.id}-occupied-${i}`}
                  />
                ) : (
                  // ✅ Empty seat - show dashed border
                  <div
                    className="w-7 h-7 rounded-full border border-dashed border-white/20"
                    title="Open seat"
                    aria-label="Open seat"
                    data-testid={`table-${table.id}-empty-${i}`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Footer: seat count + action button ── */}
      <div className="mt-auto flex items-center justify-between border-t border-white/[0.06] pt-5">
        <div className="flex items-center gap-2 text-sm text-white/80 flex-wrap">
          <Users size={15} className="text-[#C6A87C]" aria-hidden="true" />
          <span data-testid={`table-seats-${table.id}`} className="font-medium">
            {taken}/{seats} seated
          </span>
          {seatsLeft > 0 && (
            <span className="text-[#7BC8A6]">· {seatsLeft} open</span>
          )}
          {seatsLeft === 0 && <span className="text-white/40">· full</span>}
          {waitlistCount > 0 && (
            <span
              className="text-[#C6A87C]"
              data-testid={`table-waitlist-${table.id}`}
            >
              · {waitlistCount} waitlisted
            </span>
          )}
        </div>

        {joined ? (
          <button
            data-testid={`switch-table-${table.id}`}
            onClick={onSwitch}
            className="text-xs flex items-center gap-1 text-[#C6A87C] hover:text-[#D9BC93]"
          >
            Leave table <ArrowRight size={12} />
          </button>
        ) : seatsLeft === 0 ? (
          <button
            data-testid={`waitlist-toggle-${table.id}`}
            onClick={handleWaitlistToggle}
            className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#C6A87C]/50 text-[#C6A87C] hover:bg-[#C6A87C]/10 transition-colors"
          >
            <Clock size={12} aria-hidden="true" /> Join waitlist
          </button>
        ) : (
          // ── Join button — opens networking modal, disabled while API is in-flight
          <button
            data-testid={`join-table-${table.id}`}
            onClick={handleJoinClick}
            disabled={joinLoading}
            className={`text-xs flex items-center gap-1 px-3 py-1.5 rounded-full transition-colors font-semibold
              ${
                joinLoading
                  ? "bg-[#C6A87C]/50 text-[#061412]/50 cursor-not-allowed"
                  : "bg-[#C6A87C] text-[#061412] hover:bg-[#D9BC93]"
              }`}
          >
            {joinLoading ? (
              "Joining…"
            ) : (
              <>
                Pull up a chair <ArrowRight size={12} />
              </>
            )}
          </button>
        )}
      </div>

      {/* ── Waitlist form ── */}
      {waitlistOpen && !waitlistDone && (
        <form
          onSubmit={submitWaitlist}
          className="mt-4 grid grid-cols-1 gap-2"
          data-testid={`waitlist-form-${table.id}`}
        >
          <input
            required
            placeholder="Your name"
            value={waitlistName}
            onChange={(e) => setWaitlistName(e.target.value)}
            data-testid={`waitlist-name-${table.id}`}
            className="bg-white/[0.04] border border-white/[0.1] rounded-lg px-3 py-2 text-xs focus:border-[#C6A87C] focus:outline-none transition-colors"
          />
          <input
            required
            type="email"
            placeholder="you@email.com"
            value={waitlistEmail}
            onChange={(e) => setWaitlistEmail(e.target.value)}
            data-testid={`waitlist-email-${table.id}`}
            className="bg-white/[0.04] border border-white/[0.1] rounded-lg px-3 py-2 text-xs focus:border-[#C6A87C] focus:outline-none transition-colors"
          />
          <button
            type="submit"
            className="btn-gold text-xs justify-center"
            data-testid={`waitlist-submit-${table.id}`}
          >
            Join waitlist
          </button>
        </form>
      )}

      {waitlistDone && (
        <p
          className="mt-4 text-xs text-[#C6A87C]"
          data-testid={`waitlist-done-${table.id}`}
        >
          You're on the waitlist. We'll email you if a seat opens.
        </p>
      )}

      {/* ── Sponsor moment modal ── */}
      {momentOpen && sponsorMoment && (
        <SponsorMomentModal
          tableId={table.id}
          eventId={event?.id}
          sponsor={sponsorMoment}
          onClose={handleMomentClose}
        />
      )}

      {/* ── Networking details modal ──────────────────────────────────────────
           Rendered via createPortal so it mounts directly on document.body,
           escaping this card's overflow:hidden and any stacking context.
      ─────────────────────────────────────────────────────────────────────── */}
      {detailsModalOpen &&
        createPortal(
          <NetworkingDetailsModal
            open={detailsModalOpen}
            initial={lastNetworkingDetails}
            tableName={stripHtml(table.topic || table.tableName || "")}
            onCancel={handleNetworkingCancel}
            onSubmit={handleNetworkingSubmit}
          />,
          document.body,
        )}
    </div>
  );
}
